import React, { useEffect, useId, useState } from 'react'
import { ArrowRight, Menu, X } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import ThemeToggle from './ThemeToggle'
import ConnectWallet from './ConnectWallet'
import PlanBadge from './PlanBadge'
import SubscriptionTerminal from './SubscriptionTerminal'
import { useSubscriptionTerminal } from '../contexts/SubscriptionTerminalContext'

const LANDING_PAGE_URL = 'https://safepsy.com'

const Header: React.FC = () => {
  const { pathname } = useLocation()
  const menuId = useId()
  const [menuOpen, setMenuOpen] = useState(false)
  const showTryBeta = pathname === '/landing' || pathname === '/explore' || pathname === '/register' || pathname === '/faq' || pathname === '/support' || pathname === '/blog' || pathname.startsWith('/blog/')
  const isBetaChat = pathname === '/'
  const {
    showSubscriptionTerminal,
    subscriptionTerminalForced,
    openSubscriptionTerminal,
    closeSubscriptionTerminal,
  } = useSubscriptionTerminal()

  useEffect(() => {
    setMenuOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!menuOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [menuOpen])

  const openTerminal = () => {
    setMenuOpen(false)
    openSubscriptionTerminal()
  }

  return (
    <header className="relative z-50 max-w-6xl mx-auto w-full px-4 sm:px-6 py-2 sm:py-3 fade-in">
      <div className="flex items-center justify-between gap-2 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-3 text-heading text-base sm:text-lg min-w-0">
          <a href={LANDING_PAGE_URL} className="h-10 sm:h-12 transition-all duration-300 min-h-[44px] flex items-center group">
            <img
              src="/LogoTransparent1.png"
              alt="SafePsy Logo"
              className="h-10 sm:h-12 w-auto max-w-[10rem] sm:max-w-none transition-all duration-300 group-hover:drop-shadow-lg group-hover:scale-105"
            />
          </a>
        </div>

        <div className="relative flex items-center justify-end md:min-w-0 md:flex-1">
          <button
            type="button"
            className="md:hidden relative z-50 p-2 rounded-lg bg-white/70 backdrop-blur-sm border border-neutral-dark/20 hover:bg-white/90 transition-all duration-300 shadow-sm hover:shadow-md active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:bg-black/20 dark:border-white/20 dark:hover:bg-black/30 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-expanded={menuOpen}
            aria-controls={menuId}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? (
              <X className="w-5 h-5 text-text-primary dark:text-white" aria-hidden="true" />
            ) : (
              <Menu className="w-5 h-5 text-text-primary dark:text-white" aria-hidden="true" />
            )}
          </button>

          {menuOpen ? (
            <button
              type="button"
              className="md:hidden fixed inset-0 z-40 cursor-default bg-black/20"
              aria-label="Close menu"
              tabIndex={-1}
              onClick={() => setMenuOpen(false)}
            />
          ) : null}

          <div
            id={menuId}
            className={
              menuOpen
                ? 'absolute right-0 top-[calc(100%+0.5rem)] z-50 flex w-[min(18rem,calc(100vw-2rem))] flex-col items-stretch gap-3 rounded-2xl border border-neutral-dark/20 bg-white/95 p-3 shadow-xl backdrop-blur-md dark:border-white/20 dark:bg-neutral-950/95 md:static md:z-auto md:w-auto md:flex-row md:items-center md:justify-end md:gap-3 md:rounded-none md:border-0 md:bg-transparent md:p-0 md:shadow-none md:backdrop-blur-none'
                : 'hidden md:flex md:min-w-0 md:flex-1 md:items-center md:justify-end md:gap-3'
            }
          >
            {showTryBeta ? (
              <Link
                to="/"
                className="btn-try-beta w-full md:w-auto"
                aria-label="Try the SafePsy chat"
                onClick={() => setMenuOpen(false)}
              >
                Try the chat
                <ArrowRight className="w-5 h-5 shrink-0" aria-hidden="true" />
              </Link>
            ) : null}

            {isBetaChat ? (
              <div id="wallet-connect-region" className="flex w-full min-w-0 flex-col items-stretch gap-3 md:w-auto md:flex-row md:items-center md:gap-3">
                <div className="w-full md:w-auto [&_button]:w-full md:[&_button]:w-auto">
                  <PlanBadge onOpenTerminal={openTerminal} />
                </div>
                <div className="w-full min-w-0 md:w-auto md:shrink [&_button]:w-full md:[&_button]:w-auto">
                  <ConnectWallet variant="header" />
                </div>
              </div>
            ) : null}

            <div className="flex w-full md:w-auto [&_button]:w-full md:[&_button]:w-auto">
              <ThemeToggle />
            </div>
          </div>
        </div>
      </div>

      {isBetaChat && showSubscriptionTerminal ? (
        <SubscriptionTerminal
          forced={subscriptionTerminalForced}
          onActivate={() => openSubscriptionTerminal(false)}
          onClose={() => closeSubscriptionTerminal()}
        />
      ) : null}
    </header>
  )
}

export default Header
